import { useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import CheckoutModal from './CheckoutModal';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);

  const handleWhatsAppOrder = () => {
    if (cartItems.length === 0) return;

    let message = '🌿 *NEW ORDER - ORGANIC NATURAL SOLUTION*\n';
    message += '━━━━━━━━━━━━━━━━━━━━━━\n\n';
    message += '📦 *ORDER DETAILS:*\n\n';
    
    cartItems.forEach((item, index) => {
      message += `${index + 1}. *${item.name}*\n`;
      message += `   • Quantity: ${item.quantity}\n`;
      message += `   • Unit Price: ₦${item.priceNumeric.toLocaleString()}\n`;
      message += `   • Subtotal: ₦${(item.priceNumeric * item.quantity).toLocaleString()}\n\n`;
    });

    message += '━━━━━━━━━━━━━━━━━━━━━━\n';
    message += `💰 *TOTAL AMOUNT: ₦${getCartTotal().toLocaleString()}*\n`;
    message += '━━━━━━━━━━━━━━━━━━━━━━\n\n';
    message += '📍 Please confirm this order and provide your delivery address.\n\n';
    message += 'Thank you for choosing Organic Natural Solution! 🌿';

    const whatsappUrl = `https://wa.me/2348154358746?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Shopping Cart</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors active:bg-gray-100 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mb-4">
              <i className="ri-shopping-cart-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-base mb-2">Your cart is empty</p>
            <p className="text-gray-400 text-sm text-center">Add some products to get started</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex gap-3 mb-4 pb-4 border-b border-gray-200 last:border-0">
                  <div className={`w-20 h-20 rounded-lg ${item.bg} flex-shrink-0 overflow-hidden`}>
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm line-clamp-2">{item.name}</h3>
                    <p className="text-teal-600 font-bold mb-2 text-sm">{item.price}</p>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-lg"></i>
                        </button>
                        <span className="w-10 text-center font-medium text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-lg"></i>
                        </button>
                      </div>
                      
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="w-9 h-9 flex items-center justify-center text-red-500 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 p-4 bg-white">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600 text-sm">Total</span>
                <span className="text-xl font-bold text-gray-900">₦{getCartTotal().toLocaleString()}</span>
              </div>
              
              <button
                onClick={clearCart}
                className="w-full mb-2 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 active:bg-gray-200 transition-colors whitespace-nowrap text-sm cursor-pointer"
              >
                Clear Cart
              </button>

              <button
                onClick={handleWhatsAppOrder}
                className="w-full mb-2 py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Order via WhatsApp
              </button>

              <button
                onClick={() => setShowCheckout(true)}
                className="w-full py-3 px-4 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 active:bg-teal-800 transition-colors whitespace-nowrap text-sm shadow-lg cursor-pointer"
              >
                Pay Now
              </button>
            </div>
          </>
        )}
      </div>

      <CheckoutModal isOpen={showCheckout} onClose={() => setShowCheckout(false)} />
    </>
  );
}
